/**
 * Foundation date-time service shared by every admin surface.
 * Business timestamps are persisted with an explicit +08:00 offset and are
 * always rendered in Asia/Shanghai as YYYY/MM/DD HH:mm, independent of the
 * browser or CI machine timezone.
 */
export const SHANGHAI_TIME_ZONE = 'Asia/Shanghai'

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: SHANGHAI_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

const DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: SHANGHAI_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

interface ShanghaiParts {
  year: string
  month: string
  day: string
  hour: string
  minute: string
}

function shanghaiParts(date: Date, includeTime = true): ShanghaiParts {
  const parts = (includeTime ? DATE_TIME_FORMATTER : DATE_FORMATTER).formatToParts(date)
  const values: Record<string, string> = {}
  for (const part of parts) {
    if (part.type !== 'literal') values[part.type] = part.value
  }
  return {
    year: values.year ?? '0000',
    month: values.month ?? '00',
    day: values.day ?? '00',
    hour: values.hour ?? '00',
    minute: values.minute ?? '00',
  }
}

export function parseBusinessDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  if (typeof value !== 'string') return null
  const text = value.trim()
  if (!text) return null
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? `${text}T00:00:00+08:00`
    : /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?$/.test(text)
      ? `${text.replace(' ', 'T')}+08:00`
      : text
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDateTime(value: unknown): string {
  const date = parseBusinessDate(value)
  if (!date) return '—'
  const p = shanghaiParts(date)
  return `${p.year}/${p.month}/${p.day} ${p.hour}:${p.minute}`
}

export function formatDate(value: unknown): string {
  const date = parseBusinessDate(value)
  if (!date) return '—'
  const p = shanghaiParts(date, false)
  return `${p.year}/${p.month}/${p.day}`
}

/** Value shape expected by el-date-picker datetime, always Shanghai time. */
export function formatDateTimeInput(value: unknown): string {
  const date = parseBusinessDate(value)
  if (!date) return ''
  const p = shanghaiParts(date)
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:00`
}

export function nowIso(): string {
  const local = new Date(Date.now() + 8 * 3_600_000)
  const pad = (value: number): string => String(value).padStart(2, '0')
  const base = `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}T${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}:${pad(local.getUTCSeconds())}`
  return `${base}+08:00`
}

/** YYYY-MM-DD value for date inputs, always based on the Shanghai calendar day. */
export function todayDateInput(): string {
  return formatDate(Date.now()).replace(/\//g, '-')
}
